// Tiny DOM helpers: escaped template tag, toasts, modal.
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

class Raw { constructor(s) { this.s = s; } }
export const raw = (s) => (s instanceof Raw ? s : new Raw(String(s)));

/** html`<p>${userText}</p>` — interpolations are escaped unless wrapped in raw() or arrays of raw. */
export function html(strings, ...vals) {
  let out = '';
  strings.forEach((s, i) => {
    out += s;
    if (i < vals.length) {
      const v = vals[i];
      if (v instanceof Raw) out += v.s;
      else if (Array.isArray(v)) out += v.map((x) => (x instanceof Raw ? x.s : esc(x))).join('');
      else out += esc(v);
    }
  });
  return new Raw(out);
}

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export function render(root, tpl) {
  root.innerHTML = tpl instanceof Raw ? tpl.s : String(tpl);
}

let toastTimer;
export function toast(msg, { action, onAction, ms = 4000 } = {}) {
  const el = $('#toast');
  el.innerHTML = '';
  const span = document.createElement('span');
  span.textContent = msg;
  el.appendChild(span);
  if (action) {
    const b = document.createElement('button');
    b.textContent = action;
    b.onclick = () => { el.hidden = true; onAction?.(); };
    el.appendChild(b);
  }
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { el.hidden = true; }, ms);
}

/** Modal with arbitrary body html. Resolves when closed; `onMount(dialogEl, close)` wires events. */
export function modal(bodyTpl, onMount) {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'modal';
    dlg.innerHTML = bodyTpl instanceof Raw ? bodyTpl.s : bodyTpl;
    document.body.appendChild(dlg);
    const close = (v) => { dlg.close(); dlg.remove(); resolve(v); };
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(undefined); });
    dlg.addEventListener('click', (e) => { if (e.target === dlg) close(undefined); });
    dlg.showModal();
    onMount?.(dlg, close);
  });
}

export function confirmDialog(message, okLabel, cancelLabel, danger = false) {
  return modal(
    html`<p class="modal-msg">${message}</p>
      <div class="row end"><button class="btn" data-x="no">${cancelLabel}</button>
      <button class="btn ${danger ? 'danger' : 'primary'}" data-x="yes">${okLabel}</button></div>`,
    (d, close) => {
      d.querySelector('[data-x=no]').onclick = () => close(false);
      d.querySelector('[data-x=yes]').onclick = () => close(true);
    },
  );
}

import { locale } from './i18n.js';
// Times are shown in the device's own clock/time zone, the same one the time inputs use.
export const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString(locale(), { hour: 'numeric', minute: '2-digit' }) : '';
const p2 = (n) => String(n).padStart(2, '0');
/** ISO -> 'HH:MM' for <input type=time> (local). */
export const toTimeValue = (iso) => { if (!iso) return ''; const d = new Date(iso); return `${p2(d.getHours())}:${p2(d.getMinutes())}`; };
/** 'HH:MM' on the same local day as `refIso` -> ISO string. */
export function fromTimeValue(hhmm, refIso) {
  const [h, m] = hhmm.split(':').map(Number);
  const d = refIso ? new Date(refIso) : new Date();
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}
export const fmtDate = (d) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(locale(), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};
export const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
export const fullName = (s) => `${s.first_name} ${s.last_name}`.trim();
export function fmtMinutes(m) {
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
}

/** Material Symbols Rounded glyph (self-hosted subset in /fonts.css). */
export const icon = (name, cls = '') => raw(`<span class="ms ${cls}" aria-hidden="true">${name}</span>`);
export const initials = (s) => `${(s.first_name || '')[0] || ''}${(s.last_name || '')[0] || ''}`.toUpperCase() || '?';
export function applyTheme(mode) {
  try { mode ? localStorage.setItem('atnd-theme', mode) : localStorage.removeItem('atnd-theme'); } catch { /* ignore */ }
  const light = mode === 'light' || (mode !== 'dark' && matchMedia('(prefers-color-scheme: light)').matches);
  document.documentElement.classList.toggle('light', light);
}
export const getTheme = () => { try { return localStorage.getItem('atnd-theme') || 'auto'; } catch { return 'auto'; } };
export function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

/** h:mm:ss (or m:ss under an hour). */
export function fmtElapsed(ms) {
  const sec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${p(m)}:${p(s)}` : `${m}:${p(s)}`;
}
/** Short coloured badge with the class's first letters (like CodingBubble's tool badges). */
export const classBadge = (c, size = '') => raw(`<span class="cbadge ${size}" style="--bc:${esc(c.color)}" aria-hidden="true">${esc(((c.name || '?').replace(/[^\p{L}\p{N} ]/gu, '').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2) || '?').toUpperCase())}</span>`);
